"use client"

import React, { createContext, useContext, useEffect, useState } from 'react'

type Theme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  toggleTheme: () => void
  setTheme: (theme: Theme) => void
  clearTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// Helper function to get theme from cookie/localStorage synchronously
const getInitialTheme = (): Theme => {
  if (typeof window !== 'undefined') {
    const cookieMatch = document.cookie.match(/(?:^|; )theme=([^;]+)/)
    const cookieTheme = cookieMatch ? (decodeURIComponent(cookieMatch[1]) as Theme) : undefined
    const savedTheme = (localStorage.getItem('theme') as Theme) || cookieTheme
    return savedTheme || 'light'
  }
  return 'light'
}

// Helper function to apply theme to document
const applyTheme = (theme: Theme) => {
  if (typeof window !== 'undefined') {
    const root = document.documentElement
    if (theme === 'dark') {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Get initial theme synchronously to prevent flash
  const [theme, setThemeState] = useState<Theme>(getInitialTheme)

  // Apply theme immediately on mount
  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme)
    localStorage.setItem('theme', newTheme)
    try {
      document.cookie = `theme=${encodeURIComponent(newTheme)}; path=/; max-age=31536000; samesite=lax`
    } catch {}
    applyTheme(newTheme)
  }

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark'
    setTheme(newTheme)
  }

  const clearTheme = () => {
    setThemeState('dark')
    localStorage.removeItem('theme')
    try {
      document.cookie = 'theme=; path=/; max-age=0; samesite=lax'
    } catch {}
    applyTheme('dark')
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme, clearTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
} 